import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { kycApi } from '@/api';
import { QuestionnaireView } from '@/modules/Questionnaire/View';
import { kycHelpers } from '@/utils/helpers/kyc';
import { processAnswersWithFiles } from '@/utils/helpers/kyc/answerProcessing';
import { BREAKPOINTS } from '@/ui/layout';
import { STATUSES_WITH_ANSWERS, STATUS } from '@/utils/static/kyc';
import { useSelector } from 'react-redux';
import { useLoadingState, useScrollToTop } from '@/hooks';
import styled from 'styled-components';
import { usePopups, useEntityStatus, useLoadingInit } from '@/hooks';
import { useScoring } from '@/modules/Questionnaire/hooks';
import { useQuestionnaire } from '@/modules/Questionnaire/hooks/useQuestionnaire';
import {
  useEntityNavigation,
  useRejectedQuestions,
  useEntitySections,
  useReferralToken
} from '@/modules/shared/hooks';

export const Questionnaire = ({
  adminView,
  adminEditView = false,
  referralView = false,
  investorId,
  questionnaireId
}) => {
  const { referralToken } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { loader } = useSelector((state) => state.entities);
  const { id: paramId, referralId } = useParams();
  const id = adminView ? questionnaireId : paramId;
  const { showErrorToast, hideLoader, showLoader } = usePopups();
  const investor = useSelector((state) => state.investor);
  const [preFilledQuestions, setPreFilledQuestions] = useState({});
  const [creators, setCreators] = useState({});
  const [defaultValues, setDefaultValues] = useState(null);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [error, setError] = useState(undefined);

  const { checkQuestionnaireStatus } = useQuestionnaire({ adminView, referralView });

  const { rejectedQuestions, rejectedSectionsIndexes, processRejectedQuestions } =
    useRejectedQuestions({
      entityType: 'questionnaire',
      adminView,
      investorId
    });

  const {
    entity: questionnaire,
    isFetching,
    refetch: refetchSections
  } = useEntitySections({
    entityType: 'questionnaire',
    id,
    adminView,
    referralView,
    investorId,
    referralToken,
    onSuccess: async ({ data: questionnaireData }) => {
      if (
        !fetchedOnce ||
        (adminEditView &&
          (questionnaireData?.status === STATUS.QUESTION_UNACCEPTED ||
            questionnaireData?.status === STATUS.REJECTED))
      ) {
        if (
          questionnaireData?.status === STATUS.QUESTION_UNACCEPTED ||
          questionnaireData?.status === STATUS.REJECTED
        ) {
          await processRejectedQuestions(
            questionnaireData,
            questionnaireData.status,
            showErrorToast,
            hideLoader
          );
        }
      }
    }
  });

  const { dataScoring, isTotalScoreLoading, refetchScoring } = useScoring({
    adminView,
    adminEditView,
    referralView,
    investorId,
    questionnaireId: id,
    questionnaire
  });

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, submitCount, dirtyFields },
    reset,
    setValue
  } = useForm({ defaultValues: { ...defaultValues } });

  const watchedFields = useWatch({
    control,
    name: questionnaire
      ? questionnaire.questionSections.flatMap(
          (step) => step.visibleConditions?.map((condition) => condition.id) || []
        )
      : []
  });

  // Для Questionnaire нужна фильтрация секций по watchedFields
  const filteredQuestionnaire = useMemo(() => {
    if (questionnaire && questionnaire.questionSections)
      return questionnaire.questionSections.filter((item) =>
        kycHelpers.conditioningSteps(watchedFields, item.visibleConditions, item.visibleOperator)
      );
    return [];
  }, [questionnaire, watchedFields]);

  const {
    currentSection,
    setCurrentSection,
    initialStep,
    setInitialStep,
    onBack,
    finish,
    nextStep: navigationNextStep
  } = useEntityNavigation({
    sections: filteredQuestionnaire,
    rejectedSectionsIndexes,
    onFinish: async (answersIds) => {
      await checkQuestionnaireStatus({
        id: id,
        ...(answersIds && answersIds.length > 0 && { rejectedSettings: answersIds }),
        investorId: investorId
      });
      adminView
        ? navigate(`/investor/${investorId}/questionnaires`)
        : referralView
          ? navigate(`/referral/${referralId}/questionnaires`)
          : navigate('/profile/questionnaires');
    },
    onStepChange: () => {
      if (
        questionnaire?.isScoring &&
        (questionnaire.isSelectTotalPoints || questionnaire.isSelectCategoryScoring)
      ) {
        refetchScoring();
      }
      refetchSections();
    }
  });

  const { isInProcess, isRejected } = useEntityStatus(questionnaire);

  useEffect(() => {
    setFetchedOnce(false);
    setDefaultValues(null);
    setPreFilledQuestions({});
    setCreators({});
    setCurrentSection(0);
    setInitialStep(0);
  }, [id]);

  useEffect(() => {
    if (!fetchedOnce) {
      try {
        if (questionnaire && (STATUSES_WITH_ANSWERS[questionnaire.status] || adminView)) {
          const { values, lastAnsweredSection, preFilledQuestions, creators } =
            kycHelpers.collectQuestionnaireAnswers(questionnaire);
          if (
            (!adminView && (isInProcess || isRejected)) ||
            (adminView && adminEditView && (isInProcess || isRejected))
          ) {
            const filtered = questionnaire.questionSections.filter((item) =>
              kycHelpers.conditioningSteps(
                watchedFields,
                item.visibleConditions,
                item.visibleOperator
              )
            );
            setCurrentSection(Math.min(filtered.length - 1, lastAnsweredSection + 1));
            setInitialStep(Math.min(filtered.length - 1, lastAnsweredSection + 1));
          }

          if (Object.keys(preFilledQuestions).length > 0) {
            setPreFilledQuestions(preFilledQuestions);
          }
          if (Object.keys(creators).length > 0) {
            setCreators(creators);
          }

          setDefaultValues(values);
          reset(values);
          setFetchedOnce(true);
        }
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    }
  }, [
    questionnaire,
    adminView,
    adminEditView,
    fetchedOnce,
    isInProcess,
    isRejected,
    watchedFields,
    setCurrentSection,
    setInitialStep,
    reset,
    showErrorToast
  ]);

  useLoadingInit();
  useLoadingState(isFetching);

  useScrollToTop([currentSection]);

  useReferralToken({
    paramId,
    referralToken,
    referralView,
    referralId,
    onError: (errorMessage) => setError(errorMessage),
    onSuccess: () => {
      if (error) setError(undefined);
    }
  });

  const onNext = async (data, isFinish) => {
    try {
      showLoader('Processing');

      const dirtyFieldsValues = kycHelpers.getDirtyValues(dirtyFields, data);

      const { array } = kycHelpers.collectQuestionnaireCurrentAnswers({
        filteredQuestionnaire,
        questionnaire,
        data,
        currentSection,
        dirtyFieldsValues
      });

      const answersIds = [];

      const { answersArray, files } = await processAnswersWithFiles(array);

      try {
        const res = adminView
          ? await kycApi.saveAnswersForAdmin({
              answers: answersArray,
              files: files,
              investorId: investorId
            })
          : referralView
            ? await kycApi.saveReferralAnswers({
                answers: answersArray,
                files: files,
                token: referralToken,
                creatorId: investor.id
              })
            : await kycApi.saveAnswers({
                answers: answersArray,
                files: files
              });

        if (res && rejectedQuestions) {
          res.data.forEach((item) => {
            kycHelpers.getRejectedAnswersIds(questionnaire, item.questionId, answersIds, res.data);
          });
        }

        if (res && rejectedQuestions && !adminView && !referralView) {
          const fixedAnswersIds = kycHelpers.collectFixedAnswersIds(
            questionnaire,
            rejectedQuestions,
            answersArray,
            res.data
          );

          if (fixedAnswersIds.length > 0) {
            try {
              await kycApi.markAnswersAsFixed({
                answersId: fixedAnswersIds,
                questionSectionTypeId: id
              });
            } catch (e) {
              console.error('Failed to mark answers as fixed', e);
            }
          }
        }
      } catch (e) {
        showErrorToast(e, true);
        console.error(e);
      }

      isFinish ? await finish(answersIds) : await navigationNextStep(answersIds);
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  const handleNext = async (data) => {
    await onNext(data, false);
  };
  const handleFinish = async (data) => {
    await onNext(data, true);
  };

  const loading =
    isTotalScoreLoading ||
    isFetching ||
    !questionnaire ||
    questionnaire.investorQuestionSectionTypeId !== id;

  return (
    <Wrapper>
      {!loading && (
        <QuestionnaireView
          adminEditView={adminEditView}
          referralView={referralView}
          questionnaire={questionnaire}
          filteredQuestionnaire={filteredQuestionnaire}
          currentSection={currentSection}
          currentStep={initialStep}
          onNext={handleNext}
          onFinish={handleFinish}
          onBack={onBack}
          rejectedQuestions={rejectedQuestions}
          setCurrentSection={setCurrentSection}
          disabled={
            investor.accountStatus !== 'ACTIVE' ||
            (adminView || adminEditView
              ? false
              : investor.isAccessibleWithoutVerification
                ? false
                : !investor.isUserVerified)
          }
          disabledNext={loader.visible}
          control={control}
          handleSubmit={handleSubmit}
          trigger={trigger}
          watch={watch}
          setValue={setValue}
          errors={errors}
          submitCount={submitCount}
          preFilledQuestions={preFilledQuestions}
          creators={creators}
          scoring={dataScoring ? dataScoring.data : null}
        />
      )}
    </Wrapper>
  );
};

export * from './View';

const Wrapper = styled.div`
  position: relative;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    height: 100%;
  }
`;
